/**
 * Module-level cache for agency application detail objects fetched from
 * /integrations/hub/applications/<number>/detail/.
 *
 * Storing this outside React state ensures the cache survives navigation
 * without requiring a context provider, and a single Map is shared across all
 * component instances for the lifetime of the page session.
 *
 * TTL is 30 seconds. After expiry, AgencyApplicationView falls back to its
 * own individual API + Salesforce sync call.
 */

import type { Application } from "@/pages/portal/types";

interface CacheEntry {
    application: Application;
    fetchedAt: number;
}

const CACHE_TTL_MS = 30_000;

const cache = new Map<string, CacheEntry>();

if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__agencyAppDetailCache = cache;
}

/**
 * Returns the cached application for the given application number, or null if
 * the entry is missing or older than CACHE_TTL_MS.
 */
export function getCachedAgencyApplicationDetail(applicationNumber: string): Application | null {
    const entry = cache.get(applicationNumber);
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
        cache.delete(applicationNumber);
        return null;
    }
    return entry.application;
}

/**
 * Stores the full application object. Called by AgencyApplicationView after
 * a successful detail fetch or force-sync.
 */
export function setCachedAgencyApplicationDetail(applicationNumber: string, application: Application): void {
    cache.set(applicationNumber, { application, fetchedAt: Date.now() });
}

/**
 * Forcefully removes a cached entry so the next AgencyApplicationView visit
 * triggers a fresh fetch.
 */
export function invalidateCachedAgencyApplicationDetail(applicationNumber: string): void {
    cache.delete(applicationNumber);
}
